import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { FunctionalRootState, getPreferenceFileUrlFromAccount, Toaster } from '@share/utils';
import { ILoginState, signUp, handleGoogleSignUp } from '@share/store/slices';
import { Routes } from '@share/constants';

import AshevillePlayMain from '@assets/images/ashevilleplay/asheville-hero.jpg';

import './style.scss';
import GoogleButton from '../google-auth/googleAuth';
import { BACKGROUND_PHOTO_FIELD_KEY, IAccount } from '@share/common-types';
import { ProcessData } from '../../../pages/app/utils';

export function SignUpPageAshevillePlay() {

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const history = useHistory();

  const dispatch = useDispatch();

  const { account, loading } = loginStore;

  const onNavigateToLogin = () => {
    history.push(`/${account?.name}${Routes.Login}`);
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    agreeToTerms?: string;
    general?: string;
  }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    dispatch(signUp(formData.email, formData.firstName, formData.lastName, formData.email, formData.password, undefined, () => ProcessData(dispatch)));
  };

  const logoDB = account?.ashevillePlayGoogleLogo;

  const onNavigateToHome = () => {
    history.push(`/${account?.name}${Routes.Search}`);
  };

  const googleSignUp = async (credentialResponse: any) => {
    try {
      if (credentialResponse?.credential) {
        dispatch(handleGoogleSignUp(credentialResponse.credential, () => ProcessData(dispatch)));
      } else {
        Toaster.error('Google sign-up failed. Please try again.');
      }
    } catch (error) {
      console.error('Google sign-up error:', error);
      Toaster.error('Google sign-up failed. Please try again.');
    }
  };

  const onGoogleSignUpFailed = (error: any) => {
    console.error('Google sign-up failed:', error);
    Toaster.error('Google sign-up failed. Please try again.');
  };

  const backgroundImage = getPreferenceFileUrlFromAccount(account as IAccount, BACKGROUND_PHOTO_FIELD_KEY);
  const image = backgroundImage ? backgroundImage : AshevillePlayMain;

  return (
    <div className="login-page-asheville-play" style={{ backgroundImage: `linear-gradient(135deg, rgba(26, 26, 26, 0.4) 0%,rgba(26, 26, 26, 0.2) 100%), url(${image})` }}>
      <div className="logo-top-left" onClick={onNavigateToHome}>
        <img src={logoDB} alt="Asheville Play" />
      </div>

      <div className="login-card">
        <div className="login-card-header">
          <div className="logo-container">
            <h1 className="logo-text">Asheville Play</h1>
            <p className="logo-subtitle">Your Asheville starts here</p>
          </div>
          <h3>Create Account</h3>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="input-group">
                <label className="input-label" htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className={`input-text-field ${errors.firstName ? 'error' : ''}`}
                />
                {errors.firstName && <span className="input-error">{errors.firstName}</span>}
              </div>

              <div className="input-group">
                <label className="input-label" htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className={`input-text-field ${errors.lastName ? 'error' : ''}`}
                />
                {errors.lastName && <span className="input-error">{errors.lastName}</span>}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label" htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className={`input-email-field ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <span className="input-error">{errors.email}</span>}
            </div>

            <div className="input-group">
              <label className="input-label" htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Create a password"
                className={`input-password-field ${errors.password ? 'error' : ''}`}
              />
              {errors.password && <span className="input-error">{errors.password}</span>}
            </div>

            <div className="input-group">
              <label className="input-label" htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                className={`input-password-field ${errors.confirmPassword ? 'error' : ''}`}
              />
              {errors.confirmPassword && <span className="input-error">{errors.confirmPassword}</span>}
            </div>

            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', gap: '0.25rem' }}>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  style={{ marginTop: '2px', accentColor: '#A1BC6C' }}
                />
                <p className="input-label-terms" style={{ margin: 0, textAlign: 'left' }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </p>
              </label>
              {errors.agreeToTerms && <span className="input-error">{errors.agreeToTerms}</span>}
            </div>

            {errors.general && (
              <span className="input-error" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                {errors.general}
              </span>
            )}

            <button className="full-width-button" type="submit" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

          </form>

          <div className="divider-login">
            <span className="divider-text">Or sign up with</span>
          </div>

          <GoogleButton
            extraClassName="commonBlueBtn"
            onSuccess={googleSignUp}
            onFailed={onGoogleSignUpFailed}
          />

        </div>

        <div className="signup-section">
          <p className="signup-text">Already have an account?</p>
          <button className="text-button"
            type="button"
            onClick={onNavigateToLogin}
            style={{ fontSize: '1rem' }}
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
};
